import React from 'react';

export type ThemePreference = 'auto' | 'dark' | 'light';
export type DarkThemePreference = 'dark-black' | 'dark-graphite';
export type ThemeName = 'light' | DarkThemePreference;

type Theme = {
  bgNav: string;
  bgNavActiveItem: string;
  bgMain: string;
  bgMainRGB: string;
  bgSecondary: string;
  textPrimary: string;
  textPrimaryRGB: string;
  textSecondary: string;
  iconPrimary: string;
  linkUnderline: string;
  linkUnderlineRGB: string;
  linkHover: string;
  linkActive: string;
  borderColor: string;
  horizontalRule: string;
  bgHighlight: string;
  bgInverseHighlight: string;
  bgModalOverlay: string;
  bgModal: string;
  bgModalFallback: string;
  bgModalHeader: string;
  bgModalSectionHeader: string;
  bgContextMenu: string;
  bgContextMenuOpaque: string;
  bgContextMenuActiveItem: string;
  bgShortcutKey: string;
  bgShortcutKeyHovered: string;
  contextMenuBorder: string;
  shadowModal: string;
  shadowContextMenu: string;
  insetShadowContextMenu: string;
  tooltipBg: string;
  tooltipShadow: string;
  bgBadgeIcon: string;
  fillBadgeIcon: string;
  colorNoteEntity: string;
  colorNoteEntityRGB: string;
  bgNoteEntity: string;
  bgNoteEntityHover: string;
  colorDisabledNoteEntity: string;
  bgDisabledNoteEntity: string;
  bgUnfocusedFormControl: string;

  // these are intenttionally left out of the .forceDarkMode className application
  bgTourTooltip: string;
  colorTourTooltip: string;
  beforeTourTooltipSaturation: string;
  afterTourTooltipBlur: string;
  afterTourTooltipSaturation: string;
  afterTourTooltipBrightness: string;
};

export const DEFAULT_THEME_PREFERENCE = 'auto';
export const DEFAULT_DARK_THEME_PREFERENCE = 'dark-black';

// If updating dark mode names/styles, be sure to also update the .forceDarkMode names/styles. src/Styles/Common/Variables.scss
export const THEMES: Record<ThemeName, Theme> = {
  'dark-black': {
    bgNav: '#000',
    bgNavActiveItem: 'rgb(255,255,255,0.12)',
    bgMain: '#000',
    bgMainRGB: '0,0,0',
    bgSecondary: '#090a0b',
    textPrimary: '#fff',
    textPrimaryRGB: '255,255,255',
    textSecondary: 'rgba(255,255,255,0.60)',
    iconPrimary: '#fff',
    linkUnderline: '#2686be',
    linkUnderlineRGB: '38,134,190',
    linkHover: '#f2f2f2',
    linkActive: '#e5e5e5',
    borderColor: 'rgba(255,255,255,0.08)',
    horizontalRule: 'rgba(255,255,255,0.08)',
    bgHighlight: 'rgba(0,0,0,0.26)',
    bgInverseHighlight: 'rgba(255,255,255,0.2)',
    bgModalOverlay: 'rgba(0,0,0,0.6)',
    bgModal: 'rgba(255,255,255,0.08)',
    bgModalFallback: '#090909',
    bgModalHeader: 'rgba(0,0,0,0.32)',
    bgModalSectionHeader: 'rgba(0,0,0,0.16)',
    bgContextMenu: 'rgba(26,26,26,0.6)',
    bgContextMenuOpaque: 'rgba(26,26,26,1)',
    bgContextMenuActiveItem: 'rgba(255,255,255,0.08)',
    bgShortcutKey: 'rgba(0,0,0,0.6)',
    bgShortcutKeyHovered: 'rgba(0,0,0,0.72)',
    contextMenuBorder: 'rgba(255,255,255,0.08)',
    shadowModal: '0 16px 60px 30px rgba(0,0,0,0.8)',
    shadowContextMenu: '0 24px 48px rgba(0,0,0,0.24)',
    insetShadowContextMenu: 'rgba(0,0,0,0.6)',
    tooltipBg: 'rgba(255,255,255,0.08)',
    tooltipShadow: 'none',
    bgBadgeIcon: 'rgba(255,255,255,0.1)',
    fillBadgeIcon: '#fff',
    colorNoteEntity: '#2A96D4',
    colorNoteEntityRGB: '42,150,212',
    bgNoteEntity: 'rgba(42,150,212,0.16)',
    bgNoteEntityHover: 'rgba(42,150,212,0.25)',
    colorDisabledNoteEntity: 'rgba(255,255,255,0.6)',
    bgDisabledNoteEntity: 'rgba(255,255,255,0.12)',
    bgUnfocusedFormControl: 'rgb(51,51,51)',
    bgTourTooltip: '#000',
    colorTourTooltip: '#999',
    beforeTourTooltipSaturation: '1',
    afterTourTooltipBlur: '35px',
    afterTourTooltipSaturation: '1',
    afterTourTooltipBrightness: '1',
  },
  'dark-graphite': {
    bgNav: '#1D1E20',
    bgNavActiveItem: 'rgba(255,255,255,0.08)',
    bgMain: '#1D1E20',
    bgMainRGB: '29,30,32',
    bgSecondary: 'rgb(40,42,45)',
    textPrimary: '#fff',
    textPrimaryRGB: '255,255,255',
    textSecondary: 'rgba(255,255,255,0.60)',
    iconPrimary: '#fff',
    linkUnderline: '#2686be',
    linkUnderlineRGB: '38,134,190',
    linkHover: '#f2f2f2',
    linkActive: '#e5e5e5',
    borderColor: 'rgba(255,255,255,0.08)',
    horizontalRule: 'rgba(255,255,255,0.08)',
    bgHighlight: 'rgba(29,30,32,0.26)',
    bgInverseHighlight: 'rgba(255,255,255,0.2)',
    bgModalOverlay: 'rgba(0,0,0,0.60)',
    bgModal: 'rgba(255,255,255,0.08)',
    bgModalFallback: '#232527',
    bgModalHeader: 'rgba(0,0,0,0.32)',
    bgModalSectionHeader: 'rgba(0,0,0,0.16)',
    bgContextMenu: 'rgba(255,255,255,0.06)',
    bgContextMenuOpaque: 'rgb(40,42,45)',
    bgContextMenuActiveItem: 'rgba(255,255,255,0.08)',
    bgShortcutKey: 'rgba(0,0,0,0.6)',
    bgShortcutKeyHovered: 'rgba(0,0,0,0.72)',
    contextMenuBorder: 'rgba(255,255,255,0.08)',
    shadowModal: '0 16px 60px 30px rgba(0,0,0,0.2)',
    shadowContextMenu: '0 24px 48px rgba(0,0,0,0.12)',
    insetShadowContextMenu: 'rgba(0,0,0,0.6)',
    tooltipBg: 'rgba(255,255,255,0.08)',
    tooltipShadow: 'none',
    bgBadgeIcon: 'rgba(255,255,255,0.1)',
    fillBadgeIcon: '#fff',
    colorNoteEntity: '#2A96D4',
    colorNoteEntityRGB: '42,150,212',
    bgNoteEntity: 'rgba(42,150,212,0.16)',
    bgNoteEntityHover: 'rgba(42,150,212,0.24)',
    colorDisabledNoteEntity: 'rgba(255,255,255,0.6)',
    bgDisabledNoteEntity: 'rgba(255,255,255,0.12)',
    bgUnfocusedFormControl: 'rgb(51,51,51)',
    bgTourTooltip: '#000',
    colorTourTooltip: '#999',
    beforeTourTooltipSaturation: '1',
    afterTourTooltipBlur: '35px',
    afterTourTooltipSaturation: '1',
    afterTourTooltipBrightness: '1',
  },
  'light': {
    bgNav: '#f4f4f4',
    bgNavActiveItem: 'rgb(0,0,0,0.06)',
    bgMain: '#FCFCFC',
    bgMainRGB: '252,252,252',
    bgSecondary: '#f4f4f4',
    textPrimary: '#48494A',
    textPrimaryRGB: '72,73,74',
    textSecondary: '#939392',
    iconPrimary: '#828384',
    linkUnderline: '#2686be',
    linkUnderlineRGB: '38,134,190',
    linkHover: '#505253',
    linkActive: '#595b5c',
    borderColor: 'rgba(0,0,0,0.08)',
    horizontalRule: 'rgba(229,230,228,0.64)',
    bgHighlight: 'rgba(255,255,255,0.26)',
    bgInverseHighlight: 'rgba(0,0,0,0.2)',
    bgModalOverlay: 'rgba(252,252,252,0.64)',
    bgModal: 'rgba(255,255,255,0.1)',
    bgModalFallback: 'rgba(255,255,255,0.95)',
    bgModalHeader: 'rgba(232,232,231,0.4)',
    bgModalSectionHeader: 'rgba(232,232,231,0.2)',
    bgContextMenu: 'rgba(255,255,255,0.6)',
    bgContextMenuOpaque: 'rgba(255,255,255,1)',
    bgContextMenuActiveItem: 'rgba(72,73,74,0.08)',
    bgShortcutKey: 'rgba(72,73,74,0.06)',
    bgShortcutKeyHovered: 'rgba(72,73,74,0.08)',
    contextMenuBorder: 'rgba(72,73,74,0.08)',
    shadowModal: '0 16px 40px 20px rgba(0,0,0,0.12)',
    shadowContextMenu: '0 2px 4px 0 rgba(0,0,0,0.06), 0 8px 16px 4px rgba(0,0,0,0.08)',
    insetShadowContextMenu: 'rgba(255,255,255,0.6)',
    tooltipBg: 'rgba(255,255,255,0.8)',
    tooltipShadow: '0 1px 8px 4px rgba(0,0,0,0.04)',
    bgBadgeIcon: 'rgba(247,247,247,0.88)',
    fillBadgeIcon: '#8F8F8F',
    colorNoteEntity: '#2686BE',
    colorNoteEntityRGB: '38,134,190',
    bgNoteEntity: 'rgba(38,134,190,0.08)',
    bgNoteEntityHover: 'rgba(38,134,190,0.15)',
    colorDisabledNoteEntity: 'rgba(72,73,74,0.6)',
    bgDisabledNoteEntity: 'rgba(72,73,74,0.06)',
    bgUnfocusedFormControl: 'rgba(72,73,74,0.2)',
    bgTourTooltip: 'rgb(51,51,51)',
    colorTourTooltip: '#bbb',
    beforeTourTooltipSaturation: '1.5',
    afterTourTooltipBlur: '45px',
    afterTourTooltipSaturation: '1.3',
    afterTourTooltipBrightness: '1.2',
  },
};

export const setCSSVariable = (name: string, value: string) => {
  document.documentElement.style.setProperty(`--${name}`, value);
};

function setCSSVars(themeName: ThemeName, theme: Theme): void {
  const currentBodyClassNames = Array.from(document.body?.classList || []);
  const currentThemeClassName = currentBodyClassNames.find((className) =>
    className.includes('theme-'),
  );

  if (currentThemeClassName) {
    document.body.classList.remove(currentThemeClassName);
  }

  document.body.classList.add(`theme-${themeName}`);

  Object.keys(theme).forEach((themeVar) => {
    setCSSVariable(themeVar, theme[themeVar]);
  });
}

const themePreferenceKey = (userID: string): string => `clay_${userID}_theme`;
const darkThemePreferenceKey = (userID: string): string => `clay_${userID}_dark_theme_preference`;

export const themeNameFromUserPreferences = (
  themePreference: ThemePreference,
  darkThemePreference: DarkThemePreference,
): ThemeName => {
  if (themePreference === 'auto') {
    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
      return darkThemePreference;
    } else {
      return 'light';
    }
  }

  if (themePreference === 'dark') {
    return darkThemePreference;
  }

  return themePreference;
};

const themeForUserPreferences = (
  themePreference: ThemePreference,
  darkThemePreference: DarkThemePreference,
): { themeName: ThemeName; theme: Theme } => {
  const themeName = themeNameFromUserPreferences(themePreference, darkThemePreference);
  return { themeName, theme: THEMES[themeName] };
};

export const ThemeContext = React.createContext<{
  themePreference: ThemePreference;
  setThemePreference: (themePreference: ThemePreference) => void;
  darkThemePreference: DarkThemePreference;
  setDarkThemePreference: (theme: DarkThemePreference) => void;
}>({
  themePreference: DEFAULT_THEME_PREFERENCE,
  setThemePreference: (_themePreference: ThemePreference) => {},
  darkThemePreference: DEFAULT_DARK_THEME_PREFERENCE,
  setDarkThemePreference: (_darkThemePreference: DarkThemePreference) => {},
});

function getStoredThemePreference(userID: string): ThemePreference {
  let themeKey = themePreferenceKey(userID);

  if (!userID) {
    const allStorageKeys = Object.keys(window.localStorage);
    themeKey = allStorageKeys.find((key) => /_theme$/.test(key));
  }

  return (window.localStorage.getItem(themeKey) || DEFAULT_THEME_PREFERENCE) as ThemePreference;
}

export function setStoredThemePreference(userID: string, themePreference: ThemePreference) {
  window.localStorage.setItem(themePreferenceKey(userID), themePreference);
}

function getStoredDarkThemePreference(userID: string): DarkThemePreference {
  return (window.localStorage.getItem(darkThemePreferenceKey(userID)) ||
    DEFAULT_DARK_THEME_PREFERENCE) as DarkThemePreference;
}

export function setStoredDarkThemePreference(userID: string, darkThemeName: DarkThemePreference) {
  window.localStorage.setItem(darkThemePreferenceKey(userID), darkThemeName);
}

export function applyUpdatedTheme(
  themePreference: ThemePreference,
  darkThemePreference: DarkThemePreference,
) {
  const { themeName, theme } = themeForUserPreferences(themePreference, darkThemePreference);
  setCSSVars(themeName, theme);
}

export function initializeTheme(userID): [ThemePreference, DarkThemePreference] {
  const themePreference = getStoredThemePreference(userID) || DEFAULT_THEME_PREFERENCE;
  const darkThemePreference = getStoredDarkThemePreference(userID) || DEFAULT_DARK_THEME_PREFERENCE;
  const { themeName, theme } = themeForUserPreferences(themePreference, darkThemePreference);

  const toggleThemeUpdate = (e) => {
    const currentTheme = getStoredThemePreference(userID) || DEFAULT_THEME_PREFERENCE;
    const currentDarkThemePreference =
      getStoredDarkThemePreference(userID) || DEFAULT_DARK_THEME_PREFERENCE;

    if (currentTheme == 'auto') {
      if (e.matches) {
        setCSSVars(currentDarkThemePreference, THEMES[currentDarkThemePreference]);
      } else {
        setCSSVars('light', THEMES['light']);
      }
    }
  };

  window
    .matchMedia?.('(prefers-color-scheme: dark)')
    ?.addEventListener?.('change', toggleThemeUpdate);

  setCSSVars(themeName, theme);

  return [themePreference, darkThemePreference];
}

type ThemeFamily = 'light' | 'dark';

export const useTheme = (): [
  ThemeName,
  ThemeFamily,
  ThemePreference,
  (themePreference: ThemePreference) => void,
  DarkThemePreference,
  (darkThemePreference: DarkThemePreference) => void,
] => {
  const { themePreference, setThemePreference, darkThemePreference, setDarkThemePreference } =
    React.useContext(ThemeContext);
  const themeName = themeNameFromUserPreferences(themePreference, darkThemePreference);
  const themeFamily = themeName === 'light' ? 'light' : 'dark';

  return [
    themeName,
    themeFamily,
    themePreference,
    setThemePreference,
    darkThemePreference,
    setDarkThemePreference,
  ];
};
